/**
 * LineChart Component
 * Reusable line chart component using Recharts with theme support
 */

import { formatNumberEs } from '../../utils/formatters';
import React from 'react';
import styled from 'styled-components';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useThemeUtils } from '../../hooks/useTheme';
import { LoadingSpinner } from './LoadingSpinner';
import { Card } from './Card';

// ============================================================================
// Types
// ============================================================================

export interface ChartDataPoint {
  date: string;
  value: number;
  formattedDate?: string;
}

export interface LineChartProps {
  data: ChartDataPoint[];
  title: string;
  unit: string;
  loading?: boolean;
  error?: string;
  height?: number;
  color?: string;
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const ChartContainer = styled(Card)`
  padding: 1.5rem;
  height: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: var(--surface);
  border: 1px solid var(--border);
`;

const ChartHeader = styled.div`
  margin-bottom: 1rem;
`;

const ChartTitle = styled.h2`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const ChartContent = styled.div`
  flex: 1;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ErrorMessage = styled.div`
  color: var(--danger-600);
  text-align: center;
  padding: 2rem;
  font-size: 0.9rem;
`;

const EmptyMessage = styled.div`
  color: var(--text-secondary);
  text-align: center;
  padding: 2rem;
  font-size: 0.9rem;
`;

// ============================================================================
// Custom Tooltip Component
// ============================================================================

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    color?: string;
    dataKey?: string;
  }>;
  label?: string;
  unit: string;
}

const TooltipContainer = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 0.75rem;
  box-shadow: var(--shadow-lg);
  font-size: 0.875rem;
`;

const TooltipLabel = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;

const TooltipValue = styled.div`
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TooltipDot = styled.div<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
`;

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  unit,
}) => {
  if (active && payload && payload.length > 0) {
    const data = payload[0];
    if (!data) return null;

    return (
      <TooltipContainer>
        <TooltipLabel>{label}</TooltipLabel>
        <TooltipValue>
          <TooltipDot color={data.color || '#3b8a5a'} />
          {typeof data.value === 'number'
            ? formatNumberEs(data.value, 2)
            : data.value}{' '}
          {unit}
        </TooltipValue>
      </TooltipContainer>
    );
  }
  return null;
};

// ============================================================================
// Component
// ============================================================================

export const LineChart: React.FC<LineChartProps> = ({
  data,
  title,
  unit,
  loading = false,
  error,
  height = 300,
  color,
  className,
}) => {
  const { getChartColors } = useThemeUtils();
  const chartColors = getChartColors();

  const lineColor = color || chartColors.primary;

  if (loading) {
    return (
      <ChartContainer className={className}>
        <ChartHeader>
          <ChartTitle>{title}</ChartTitle>
        </ChartHeader>
        <ChartContent>
          <LoadingSpinner size="sm" />
        </ChartContent>
      </ChartContainer>
    );
  }

  if (error) {
    return (
      <ChartContainer className={className}>
        <ChartHeader>
          <ChartTitle>{title}</ChartTitle>
        </ChartHeader>
        <ChartContent>
          <ErrorMessage>{error}</ErrorMessage>
        </ChartContent>
      </ChartContainer>
    );
  }

  if (!data || data.length === 0) {
    return (
      <ChartContainer className={className}>
        <ChartHeader>
          <ChartTitle>{title}</ChartTitle>
        </ChartHeader>
        <ChartContent>
          <EmptyMessage>No hay datos disponibles para mostrar</EmptyMessage>
        </ChartContent>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer className={className}>
      <ChartHeader>
        <ChartTitle>{title}</ChartTitle>
      </ChartHeader>
      <ChartContent>
        <ResponsiveContainer width="100%" height={height}>
          <RechartsLineChart
            data={data}
            margin={{
              top: 5,
              right: 12,
              left: 0,
              bottom: 5,
            }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={chartColors.grid}
              opacity={0.5}
            />
            <XAxis
              dataKey="formattedDate"
              stroke={chartColors.text}
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              stroke={chartColors.text}
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) =>
                typeof value === 'number' ? formatNumberEs(value, 1) : value
              }
            />
            <Tooltip
              content={<CustomTooltip unit={unit} />}
              cursor={{ stroke: lineColor, strokeWidth: 1 }}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={lineColor}
              strokeWidth={2}
              dot={{ fill: lineColor, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: lineColor, strokeWidth: 2 }}
            />
          </RechartsLineChart>
        </ResponsiveContainer>
      </ChartContent>
    </ChartContainer>
  );
};
